import { localAdapter, SaveData, slugify } from './localAdapter';
import { firebaseAdapter } from './firebaseAdapter';
import { isAuthenticated, isAnonymous, getCurrentUser, waitForAuthState } from '../firebase';

// Storage service interface - can be easily swapped for Firebase later
export interface StorageAdapter {
  init(): Promise<void>;
  listProfiles(): Promise<{id: string, name: string, updatedAt: Date, lastAccessedAt: Date}[]>;
  loadProfile(id: string): Promise<SaveData | null>;
  saveProfile(id: string, data: SaveData, profileName?: string): Promise<void>;
  deleteProfile(id: string): Promise<void>;
  renameProfile(id: string, newName: string): Promise<void>;
  exportProfile(id: string): Promise<void>;
  importProfile(file: File): Promise<string>;
}

// Current storage adapter (local IndexedDB by default)
let currentAdapter: StorageAdapter = localAdapter;

// Flag to track if we're using Firebase
let usingFirebase = false;

// Initialize storage
export async function initStorage(): Promise<void> {
  await currentAdapter.init();
}

// Profile management functions
export async function listProfiles(): Promise<{id: string, name: string, updatedAt: Date, lastAccessedAt: Date}[]> {
  return await currentAdapter.listProfiles();
}

export async function loadProfile(id: string): Promise<SaveData | null> {
  console.log(`📖 [StorageService] loadProfile called - using adapter: ${currentAdapter.constructor.name}, firebase: ${isUsingFirebase()}`);
  return await currentAdapter.loadProfile(id);
}

export async function saveProfile(id: string, data: SaveData, profileName?: string): Promise<void> {
  console.log(`💾 [StorageService] saveProfile called - using adapter: ${currentAdapter.constructor.name}, firebase: ${isUsingFirebase()}`);
  await currentAdapter.saveProfile(id, data, profileName);
}

export async function deleteProfile(id: string): Promise<void> {
  await currentAdapter.deleteProfile(id);
}

export async function renameProfile(id: string, newName: string): Promise<void> {
  await currentAdapter.renameProfile(id, newName);
}

export async function createProfile(name: string, data?: SaveData): Promise<string> {
  const profileId = slugify(name);

  const defaultData: SaveData = {
    warband: {
      ducats: 700,
      glory: 0,
      battles: 0,
      name: name,
      explorationSkills: [],
      warchest: []
    },
    models: [],
    items: [],
    locations: [],
    modelTemplates: [],
    itemTemplates: []
  };

  await saveProfile(profileId, data || defaultData, name);
  return profileId;
}

export async function exportProfile(id: string): Promise<void> {
  await currentAdapter.exportProfile(id);
}

export async function importProfile(file: File): Promise<string> {
  return await currentAdapter.importProfile(file);
}

// Get last accessed profile ID (for auto-loading)
export async function getLastProfileId(): Promise<string | null> {
  try {
    const profiles = await listProfiles();
    if (profiles.length === 0) return null;

    // Return the most recently accessed profile
    return profiles[0].id;
  } catch (error) {
    console.error('Failed to get last profile:', error);
    return null;
  }
}

// Utility functions
export function generateProfileId(name: string): string {
  return slugify(name);
}

// Switch to Firebase storage (requires authentication)
export async function switchToFirebase(): Promise<void> {
  console.log('🔄 [StorageService] Switching to Firebase storage...');
  console.log('🔍 [StorageService] Auth status - authenticated:', isAuthenticated(), 'anonymous:', isAnonymous(), 'user:', getCurrentUser()?.uid);

  if (!isAuthenticated()) {
    console.error('❌ [StorageService] Cannot switch to Firebase: User not authenticated');
    throw new Error('Firebase storage requires authentication');
  }

  try {
    await firebaseAdapter.init();
    currentAdapter = firebaseAdapter;
    usingFirebase = true;

    // Force a first write to create a visible collection in Firestore console
    console.log('🎯 [StorageService] Creating guaranteed first write for Firestore visibility...');
    await forceFirstWrite();

    console.log('✅ [StorageService] Successfully switched to Firebase storage');
  } catch (error) {
    console.error('❌ [StorageService] Failed to switch to Firebase:', error);
    // Ensure we don't leave the flag in an inconsistent state
    usingFirebase = false;
    currentAdapter = localAdapter;
    throw error;
  }
}

// Switch back to local storage
export function switchToLocal(): void {
  currentAdapter = localAdapter;
  usingFirebase = false;
}

// Check if currently using Firebase
export function isUsingFirebase(): boolean {
  return usingFirebase && currentAdapter === firebaseAdapter;
}

// Future Firebase migration point
export function setStorageAdapter(adapter: StorageAdapter): void {
  currentAdapter = adapter;
  usingFirebase = adapter === firebaseAdapter;
}

// Force a first write to create visible collection in Firestore console
async function forceFirstWrite(): Promise<void> {
  try {
    // Wait for authentication to be fully established and capture the user
    const user = await waitForAuthState();
    if (!user) {
      console.warn('⚠️ [StorageService] Cannot force first write: No authenticated user');
      return;
    }

    const userId = user.uid;
    console.log(`🔐 [StorageService] Using authenticated user ID for first write: ${userId}`);

    // Create a minimal test profile that will definitely show up in console
    const testProfileId = '_firestore_test_profile_' + Date.now();
    const testProfileData: SaveData = {
      warband: {
        ducats: 1,
        glory: 0,
        battles: 0,
        name: "Firestore Connection Test",
        explorationSkills: [],
        warchest: []
      },
      models: [],
      items: [],
      locations: [],
      modelTemplates: [],
      itemTemplates: []
    };

    console.log(`🔨 [StorageService] Creating test document at users/${userId}/profiles/${testProfileId}`);

    // Use the Firebase adapter directly to bypass the current adapter check
    await firebaseAdapter.saveProfile(testProfileId, testProfileData, "Firestore Connection Test");

    // Immediately delete it to clean up
    console.log('🗑️ [StorageService] Cleaning up test document...');
    await firebaseAdapter.deleteProfile(testProfileId);

    console.log('🎉 [StorageService] First write test completed - collection should now be visible in console');
  } catch (error) {
    console.warn('⚠️ [StorageService] First write test failed (this may be expected):', error);
    // Don't throw - this is just a test write to make the collection visible
  }
}

// Export current adapter for advanced usage
export { currentAdapter };
